import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Login from './components/Login';
import Register from './components/Register';
import FileUpload from './components/FileUpload';
import BurnInUpload from './components/BurnInUpload';
import BurnInFileTable from './components/BurnInFileTable';
import {
  BurnInCategoricalValuesPage,
  BurnInControlChartPage,
  BurnInDataCompletenessPage,
  BurnInFailureParetoPage,
  BurnInHourlyActivityPage,
  BurnInLimitCompliancePage,
  BurnInNearLimitRiskPage,
  BurnInParetoChartPage,
  BurnInPieChartPage,
  BurnInRootCauseAnalyzerPage,
  BurnInSequenceBandsPage,
  BurnInSequenceHotspotsPage,
  BurnInSourceHealthPage,
  BurnInStatusPage,
  BurnInStepFailRatePage,
  BurnInStepVolumePage,
  BurnInTemperatureAnalyzerPage,
  BurnInTimelinePage,
  BurnInUUTStatusSummaryPage,
  BurnInUploadComparisonPage,
  BurnInUnitsPage,
  BurnInValueSpreadPage,
} from './components/burnIn/BurnInAnalyticsPages';
import SerialPassFailChart from './components/SerialPassFailChart';
import FileTable from './components/FileTable';
import ParetoChart from './components/ParetoChart';
import UUTStatusSummaryChart from './components/UUTStatusSummaryChart';
import ControlChart from './components/ControlChart';
import RootCausePrediction from './components/RootCausePrediction';
import PredictiveFailureAnalysis from './components/PredictiveFailureAnalysis';
import StepFrequencyChart from './components/StepFrequencyChart';
import NormalDistributionChart from './components/NormalDistributionChart';
import Dashboard from './components/Dashboard'; // Import the Dashboard component
import DashboardSelector from './components/DashboardSelector';
import BurnInDashboard from './components/BurnInDashboard';
import AdminPanel from './components/AdminPanel';
import AdminActivityPage from './components/AdminActivityPage';
import AdminUserDetailPage from './components/AdminUserDetailPage';
import AdminTeamPage from './components/AdminTeamPage';
import AdminLicenseReportPage from './components/AdminLicenseReportPage';
import AccountApprovalPage from './components/AccountApprovalPage';
import FirstLoginChecklistPage from './components/FirstLoginChecklistPage';
import LicenseAcceptancePage from './components/LicenseAcceptancePage';
import RootCauseAlarms from './components/RootCauseAlarms';
import FirstFailFingerprint from './components/FirstFailFingerprint';
import LimitMarginRisk from './components/LimitMarginRisk';
import ProductionHotspots from './components/ProductionHotspots';
import CascadeFailureSpread from './components/CascadeFailureSpread';
import RetryPressure from './components/RetryPressure';
import LateFailureCost from './components/LateFailureCost';
import LoadingScreen from './components/LoadingScreen'; // Import the LoadingScreen component
import './i18n'; // Import the i18n setup
import 'bootstrap/dist/css/bootstrap.min.css';
import NewControlChart from './components/NewControlChart';
import MlRunDetail from './pages/MlRunDetail'; // Import the MlRunDetail component form './pages/MlRunDetail'
import PageHelp from './components/PageHelp';
import AppMessageNotifier from './components/AppMessageNotifier';
import AppApprovalNotifier from './components/AppApprovalNotifier';
import { checkAuth, logoutUser, sendAuthDisconnect, sendAuthHeartbeat, sendAuthPageVisit } from './api/auth';
import GlobalLanguageToggle from './components/GlobalLanguageToggle';
import CompanyPresenceDock from './components/company/CompanyPresenceDock';
import ArtavaSupportPage from './components/artava/ArtavaSupportPage';
import SmartiLabDashboard from './features/smartiLab/SmartiLabDashboard';
import SmartiLabHome from './features/smartiLab/SmartiLabHome';
import SmartiLabDisabledPage from './features/smartiLab/SmartiLabDisabledPage';
import OperationsCenter, { OperationsSnapshotPage } from './components/OperationsCenter';
import ReportBuilderPage from './components/ReportBuilderPage';
import UnitHistoryPage from './features/smartiLab/pages/UnitHistoryPage';
import UnitDetailPage from './features/smartiLab/pages/UnitDetailPage';
import PriorityQueuePage from './features/smartiLab/pages/PriorityQueuePage';
import DataQualityPage from './features/smartiLab/pages/DataQualityPage';
import StationHealthPage from './features/smartiLab/pages/StationHealthPage';
import StationDetailPage from './features/smartiLab/pages/StationDetailPage';
import RegressionDetectorPage from './features/smartiLab/pages/RegressionDetectorPage';
import RootCauseExplorerPage from './features/smartiLab/pages/RootCauseExplorerPage';
import NearLimitDriftPage from './features/smartiLab/pages/NearLimitDriftPage';
import RepeatOffenderStepsPage from './features/smartiLab/pages/RepeatOffenderStepsPage';
import BurnInDegradationPage from './features/smartiLab/pages/BurnInDegradationPage';
import CrossDatasetLinksPage from './features/smartiLab/pages/CrossDatasetLinksPage';
import WatchlistPage from './features/smartiLab/pages/WatchlistPage';
import SavedViewsPage from './features/smartiLab/pages/SavedViewsPage';
import CasesPage from './features/smartiLab/pages/CasesPage';
import ExecutiveSummaryPage from './features/smartiLab/pages/ExecutiveSummaryPage';
import CompareWorkbenchPage from './features/smartiLab/pages/CompareWorkbenchPage';
import OpsConsolePage from './features/smartiLab/pages/OpsConsolePage';
import SuspectWorkbenchPage from './features/smartiLab/pages/SuspectWorkbenchPage';
import { isSmartiLabEnabled } from './features/smartiLab/config';
import { acceptLicense, getCurrentLicense, getLicenseStatus } from './api/license';
import { artavaBrand, getDocumentTitleForPath } from './branding/artavaBrand';

function getClientMeta() {
  const nav = window.navigator || {};
  const screenInfo = window.screen || {};

  return {
    language: nav.language || '',
    languages: Array.isArray(nav.languages) ? nav.languages.join(', ') : '',
    platform: nav.platform || '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${screenInfo.width || 0}x${screenInfo.height || 0}`,
    colorDepth: screenInfo.colorDepth || null,
    path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || '',
  };
}

function applyAdaptiveUiProfile() {
  const root = document.documentElement;
  const viewportWidth = window.innerWidth || 1440;
  const viewportHeight = window.innerHeight || 900;
  const devicePixelRatio = window.devicePixelRatio || 1;
  const shortestSide = Math.min(viewportWidth, viewportHeight);

  let scale = 1;
  let tier = 'standard';

  if (viewportWidth >= 2560) {
    scale = 1.08;
    tier = 'cinema';
  } else if (viewportWidth >= 1920) {
    scale = 1.04;
    tier = 'large';
  } else if (viewportWidth <= 1366 || viewportHeight <= 800) {
    scale = 0.94;
    tier = 'compact';
  } else if (viewportWidth <= 1536) {
    scale = 0.97;
    tier = 'balanced';
  }

  if (shortestSide <= 760) {
    scale = Math.min(scale, 0.93);
    tier = 'mobile';
  }

  if (devicePixelRatio >= 2 && viewportWidth <= 1512) {
    scale -= 0.02;
  } else if (devicePixelRatio <= 1.1 && viewportWidth >= 2200) {
    scale += 0.01;
  }

  const clampedScale = Math.max(0.92, Math.min(1.1, Number(scale.toFixed(3))));
  root.style.setProperty('--ui-scale', String(clampedScale));
  root.style.setProperty('--ui-font-size', `${(17 * clampedScale).toFixed(2)}px`);
  root.style.setProperty('--ui-control-min-height', `${Math.round(52 * clampedScale)}px`);
  root.style.setProperty('--ui-scrollbar-size', `${Math.max(10, Math.round(12 * clampedScale))}px`);
  root.style.setProperty('--ui-radius-card', `${Math.round(24 * clampedScale)}px`);
  root.style.setProperty('--ui-radius-control', `${Math.round(18 * clampedScale)}px`);
  root.dataset.viewportTier = tier;
}

const AUTH_CHECK_SPLASH_MIN_MS = 2200;

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [licenseGateLoading, setLicenseGateLoading] = useState(false);
  const [licenseGateResolved, setLicenseGateResolved] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [currentLicense, setCurrentLicense] = useState(null);
  const [licenseGateError, setLicenseGateError] = useState('');
  const [licenseFlash, setLicenseFlash] = useState('');

  const { i18n } = useTranslation();
  const smartiLabEnabled = isSmartiLabEnabled();

  useEffect(() => {
    let cancelled = false;
    let revealTimeoutId = 0;
    const startedAt = Date.now();

    const verifySession = async () => {
      try {
        const result = await checkAuth();
        if (cancelled) return;
        setIsAuthenticated(Boolean(result.authenticated));
        setCurrentUser(result.authenticated ? result.user : null);
      } catch (error) {
        if (cancelled) return;
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        const elapsed = Date.now() - startedAt;
        const remaining = Math.max(0, AUTH_CHECK_SPLASH_MIN_MS - elapsed);
        revealTimeoutId = window.setTimeout(() => {
          if (!cancelled) {
            setAuthChecked(true);
          }
        }, remaining);
      }
    };

    verifySession();

    return () => {
      cancelled = true;
      window.clearTimeout(revealTimeoutId);
    };
  }, []);

  useEffect(() => {
    let frameId = 0;
    const applyProfile = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        applyAdaptiveUiProfile();
      });
    };

    applyProfile();
    window.addEventListener('resize', applyProfile);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', applyProfile);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;
    const beat = async () => {
      try {
        const result = await sendAuthHeartbeat(getClientMeta());
        if (!cancelled && result.authenticated === false) {
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } catch (error) {
        console.warn('Heartbeat failed:', error);
      }
    };

    beat();
    const intervalId = window.setInterval(beat, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!licenseFlash) return undefined;
    const timeoutId = window.setTimeout(() => {
      setLicenseFlash('');
    }, 5000);
    return () => window.clearTimeout(timeoutId);
  }, [licenseFlash]);

  useEffect(() => {
    let cancelled = false;

    const syncLicenseGate = async () => {
      if (!isAuthenticated || !currentUser?.id) {
        if (!cancelled) {
          setLicenseGateLoading(false);
          setLicenseGateResolved(true);
          setLicenseStatus(null);
          setCurrentLicense(null);
          setLicenseGateError('');
        }
        return;
      }

      if (currentUser.role === 'admin' || !currentUser.requiresLicenseAcceptance) {
        if (!cancelled) {
          setLicenseGateLoading(false);
          setLicenseGateResolved(true);
          setLicenseStatus({
            required: false,
            accepted: true,
            exempt: true,
          });
          setLicenseGateError('');
        }
        return;
      }

      if (!cancelled) {
        setLicenseGateLoading(true);
        setLicenseGateResolved(false);
        setLicenseGateError('');
      }

      try {
        const [status, license] = await Promise.all([
          getLicenseStatus(),
          getCurrentLicense(),
        ]);
        if (cancelled) return;
        setLicenseStatus(status);
        setCurrentLicense(license);
      } catch (error) {
        if (cancelled) return;
        setLicenseGateError(error.message || 'Could not load the current Smarti license.');
      } finally {
        if (!cancelled) {
          setLicenseGateLoading(false);
          setLicenseGateResolved(true);
        }
      }
    };

    syncLicenseGate();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, currentUser?.requiresLicenseAcceptance, currentUser?.role, isAuthenticated]);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user || null);
    setLicenseFlash('');
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setLicenseFlash('');
  };

  const handleUnauthenticated = useCallback(() => {
    setIsAuthenticated(false);
    setCurrentUser(null);
  }, []);

  const handleLicenseAccepted = useCallback(async (payload) => {
    const result = await acceptLicense(payload);
    setLicenseStatus((current) => ({
      ...(current || {}),
      required: true,
      accepted: true,
      exempt: false,
      acceptedAt: result.acceptedAt || new Date().toISOString(),
      licenseVersion: result.licenseVersion,
      accessState: result.accessState || 'trial',
      access: {
        ...(current?.access || {}),
        state: result.accessState || 'trial',
        trialExpiresAt: result.trialEndsAt || null,
      },
    }));
    setLicenseGateError('');
    setCurrentUser((current) => (
      current
        ? {
            ...current,
            requiresLicenseAcceptance: true,
            licenseState: result.accessState || 'trial',
            licenseTrialExpiresAt: result.trialEndsAt || null,
          }
        : current
    ));
    if (result.trialGranted) {
      setLicenseFlash(
        i18n.language?.startsWith('it')
          ? `Licenza accettata. La prova Smarti di 7 giorni resta attiva fino al ${result.trialEndsAt ? new Date(result.trialEndsAt).toLocaleString(i18n.language || undefined) : ''}. Dopo la prova, contatta ${artavaBrand.companyName} per attivare il piano commerciale.`
          : `License accepted. Your 7-day Smarti trial is active until ${result.trialEndsAt ? new Date(result.trialEndsAt).toLocaleString(i18n.language || undefined) : ''}. After the trial, contact ${artavaBrand.companyName} to activate the commercial plan.`
      );
    }
    return result;
  }, [i18n]);

  const handleOnboardingCompleted = useCallback((updatedSessionUser) => {
    if (updatedSessionUser) {
      setCurrentUser(updatedSessionUser);
      return;
    }
    setCurrentUser((current) => (
      current
        ? { ...current, onboardingCompletedAt: new Date().toISOString() }
        : current
    ));
  }, []);

  const handleCurrentUserUpdated = useCallback((updatedSessionUser) => {
    if (updatedSessionUser) {
      setCurrentUser(updatedSessionUser);
    }
  }, []);

  // Language toggle function
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'it' : 'en';
    i18n.changeLanguage(newLang);
  };

  if (!authChecked) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
          background: 'linear-gradient(135deg, #f7f2ec 0%, #faf7f3 42%, #edf3f7 100%)',
          color: '#425466',
          fontFamily: "'Avenir Next', 'Segoe UI', Arial, sans-serif",
        }}
      >
        <div style={{ display: 'grid', justifyItems: 'center', gap: 14, textAlign: 'center' }}>
          <img
            src={artavaBrand.logoSrc}
            alt={artavaBrand.companyName}
            style={{ width: 'min(260px, 64vw)', height: 'auto' }}
          />
          <div style={{ display: 'grid', gap: 6 }}>
            <p style={{ margin: 0, color: '#102c42', fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 12 }}>
              Smarti by Artava Systems
            </p>
            <p style={{ margin: 0, color: '#425466', fontWeight: 800 }}>
              {i18n.t('Checking your session...')}
            </p>
            <a
              href={artavaBrand.websiteUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#173b57', fontWeight: 800, textDecoration: 'none' }}
            >
              {artavaBrand.websiteHost}
            </a>
          </div>
        </div>
      </div>
    );
  }

  const mustAcceptLicense =
    isAuthenticated &&
    currentUser?.role !== 'admin' &&
    currentUser?.requiresLicenseAcceptance === true &&
    !licenseGateLoading &&
    licenseGateResolved &&
    licenseStatus?.accepted !== true;
  const mustCompleteOnboarding =
    isAuthenticated &&
    currentUser?.role !== 'admin' &&
    currentUser?.onboardingCompletedAt == null &&
    !mustAcceptLicense;

  const renderProtectedElement = (element, { adminOnly = false } = {}) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (adminOnly && currentUser?.role !== 'admin') {
      return <Navigate to="/dashboard" />;
    }
    if (!licenseGateResolved && currentUser?.requiresLicenseAcceptance) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(135deg, #f7f2ec 0%, #faf7f3 42%, #edf3f7 100%)',
            color: '#425466',
            fontFamily: "'Avenir Next', 'Segoe UI', Arial, sans-serif",
            fontWeight: 800,
          }}
        >
          {i18n.t('Loading license requirements...')}
        </div>
      );
    }
    if (mustAcceptLicense) {
      return (
        <>
          {licenseGateError ? (
            <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 20, padding: '12px 16px', borderRadius: 14, background: '#fff4f4', color: '#b42318', border: '1px solid #ffc9c9', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)' }}>
              {licenseGateError}
            </div>
          ) : null}
          <LicenseAcceptancePage
            currentLicense={currentLicense}
            status={licenseStatus}
            onAccept={handleLicenseAccepted}
            loading={licenseGateLoading}
          />
        </>
      );
    }
    if (mustCompleteOnboarding) {
      return (
        <FirstLoginChecklistPage
          currentUser={currentUser}
          onComplete={handleOnboardingCompleted}
        />
      );
    }
    return (
      <>
        {licenseFlash ? (
          <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 20, padding: '12px 16px', maxWidth: 'min(92vw, 760px)', borderRadius: 14, background: '#edf8f2', color: '#24573a', border: '1px solid #badbcc', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)' }}>
            {licenseFlash}
          </div>
        ) : null}
        {element}
      </>
    );
  };

  return (
    <Router>
      <AppActivityTracker
        isAuthenticated={isAuthenticated}
        onUnauthenticated={handleUnauthenticated}
      />
      <AppDocumentTitle />
      <AppMessageNotifier isAuthenticated={isAuthenticated} />
      <AppApprovalNotifier isAuthenticated={isAuthenticated} currentUser={currentUser} />
      <CompanyPresenceDock />
      <PageHelp />
      <GlobalLanguageToggle visible={isAuthenticated} />
      <Routes>
        {/* Main entry point */}
        <Route 
          path="/" 
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} 
        />

        {/* Route for Login page */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} 
        />
        <Route
          path="/Login"
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />}
        />
        <Route path="/account-approval" element={<AccountApprovalPage />} />
        <Route path="/about-artava" element={<ArtavaSupportPage currentUser={currentUser} publicMode />} />

        {/* Loading route */}
        <Route 
          path="/loading" 
          element={isAuthenticated ? <LoadingScreen /> : <Navigate to="/login" />} 
        />

        {/* After loading, redirect to Dashboard */}
        <Route 
          path="/dashboard" 
          element={
            renderProtectedElement(
              <DashboardSelector onLogout={handleLogout} currentUser={currentUser} onCurrentUserUpdated={handleCurrentUserUpdated} />
            )
          } 
        />
        <Route
          path="/production-dashboard"
          element={
            renderProtectedElement(
              <Dashboard onLogout={handleLogout} onToggleLanguage={toggleLanguage} currentUser={currentUser} />
            )
          }
        />
        <Route
          path="/burn-in-dashboard"
          element={renderProtectedElement(<BurnInDashboard onLogout={handleLogout} />)}
        />
        <Route
          path="/smarti-lab"
          element={
            renderProtectedElement(
              smartiLabEnabled ? <SmartiLabDashboard currentUser={currentUser} /> : <SmartiLabDisabledPage />
            )
          }
        >
          <Route index element={<SmartiLabHome />} />
          <Route path="executive" element={<ExecutiveSummaryPage />} />
          <Route path="priority-queue" element={<PriorityQueuePage />} />
          <Route path="data-quality" element={<DataQualityPage />} />
          <Route path="units" element={<UnitHistoryPage />} />
          <Route path="units/:serialNumber" element={<UnitDetailPage />} />
          <Route path="suspects/:candidateId" element={<SuspectWorkbenchPage />} />
          <Route path="stations" element={<StationHealthPage />} />
          <Route path="stations/:stationName" element={<StationDetailPage />} />
          <Route path="regressions" element={<RegressionDetectorPage />} />
          <Route path="root-cause" element={<RootCauseExplorerPage />} />
          <Route path="near-limit-drift" element={<NearLimitDriftPage />} />
          <Route path="repeat-offenders" element={<RepeatOffenderStepsPage />} />
          <Route path="burn-in-degradation" element={<BurnInDegradationPage />} />
          <Route path="cross-dataset-links" element={<CrossDatasetLinksPage />} />
          <Route path="watchlist" element={<WatchlistPage />} />
          <Route path="saved-views" element={<SavedViewsPage />} />
          <Route path="cases" element={<CasesPage />} />
          <Route path="compare" element={<CompareWorkbenchPage />} />
          <Route path="ops" element={<OpsConsolePage />} />
        </Route>

        <Route
          path="/admin"
          element={
            renderProtectedElement(<AdminPanel currentUser={currentUser} />, { adminOnly: true })
          }
        />
        <Route
          path="/admin/activity"
          element={
            renderProtectedElement(<AdminActivityPage currentUser={currentUser} />, { adminOnly: true })
          }
        />
        <Route
          path="/admin/team"
          element={
            renderProtectedElement(<AdminTeamPage currentUser={currentUser} />, { adminOnly: true })
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            renderProtectedElement(<AdminUserDetailPage currentUser={currentUser} />, { adminOnly: true })
          }
        />
        <Route
          path="/admin/license-report"
          element={
            renderProtectedElement(<AdminLicenseReportPage currentUser={currentUser} />, { adminOnly: true })
          }
        />

        {/* Other routes */}
        <Route
          path="/predictive-analysis"
          element={renderProtectedElement(<PredictiveFailureAnalysis />)}
        />
<Route
  path="/ml-run-detail"
  element={renderProtectedElement(<MlRunDetail />)}
/>
<Route
  path="/ml-run-detail/:runId"
  element={renderProtectedElement(<MlRunDetail />)}
/>
<Route
  path="/ml/runs"
  element={renderProtectedElement(<MlRunDetail />)}
/>
<Route
  path="/ml/runs/:runId"
  element={renderProtectedElement(<MlRunDetail />)}
/>

        <Route
          path='/root-cause-alarms'
          element={renderProtectedElement(<RootCauseAlarms />)}
          />
        <Route
          path="/first-fail-fingerprint"
          element={renderProtectedElement(<FirstFailFingerprint />)}
        />
        <Route
          path="/limit-margin-risk"
          element={renderProtectedElement(<LimitMarginRisk />)}
        />
        <Route
          path="/production-hotspots"
          element={renderProtectedElement(<ProductionHotspots />)}
        />
        <Route
          path="/cascade-failure-spread"
          element={renderProtectedElement(<CascadeFailureSpread />)}
        />
        <Route
          path="/retry-pressure"
          element={renderProtectedElement(<RetryPressure />)}
        />
        <Route
          path="/late-failure-cost"
          element={renderProtectedElement(<LateFailureCost />)}
        />
        <Route
          path="/new-control-chart"
          element={renderProtectedElement(<NewControlChart />)}
          />
        <Route
          path="/step-frequency"
          element={renderProtectedElement(<StepFrequencyChart />)}
        />
        <Route
          path="/root-cause-prediction"
          element={renderProtectedElement(<RootCausePrediction />)}
        />
        {/* Additional Routes */}
        <Route 
          path="/register" 
          element={<Register />} 
        />
        <Route 
          path="/upload" 
          element={renderProtectedElement(<FileUpload />)} 
        />
        <Route
          path="/operations-center"
          element={renderProtectedElement(<OperationsCenter currentUser={currentUser} />)}
        />
        <Route
          path="/support/artava"
          element={renderProtectedElement(<ArtavaSupportPage currentUser={currentUser} />)}
        />
        <Route
          path="/report-builder"
          element={renderProtectedElement(<ReportBuilderPage currentUser={currentUser} />)}
        />
        <Route
          path="/ops-snapshot/:slug"
          element={renderProtectedElement(<OperationsSnapshotPage />)}
        />
        <Route
          path="/burn-in-upload"
          element={renderProtectedElement(<BurnInUpload />)}
        />
        <Route
          path="/burn-in-files"
          element={renderProtectedElement(<BurnInFileTable />)}
        />
        <Route path="/burn-in-temperature-analyzer" element={renderProtectedElement(<BurnInTemperatureAnalyzerPage />)} />
        <Route path="/burn-in-pie-chart" element={renderProtectedElement(<BurnInPieChartPage />)} />
        <Route path="/burn-in-pareto-chart" element={renderProtectedElement(<BurnInParetoChartPage />)} />
        <Route path="/burn-in-control-chart" element={renderProtectedElement(<BurnInControlChartPage />)} />
        <Route path="/burn-in-uut-status-summary" element={renderProtectedElement(<BurnInUUTStatusSummaryPage />)} />
        <Route path="/burn-in-root-cause-analyzer" element={renderProtectedElement(<BurnInRootCauseAnalyzerPage />)} />
        <Route path="/burn-in-status" element={renderProtectedElement(<BurnInStatusPage />)} />
        <Route path="/burn-in-failure-pareto" element={renderProtectedElement(<BurnInFailureParetoPage />)} />
        <Route path="/burn-in-step-fail-rate" element={renderProtectedElement(<BurnInStepFailRatePage />)} />
        <Route path="/burn-in-step-volume" element={renderProtectedElement(<BurnInStepVolumePage />)} />
        <Route path="/burn-in-timeline" element={renderProtectedElement(<BurnInTimelinePage />)} />
        <Route path="/burn-in-hourly-activity" element={renderProtectedElement(<BurnInHourlyActivityPage />)} />
        <Route path="/burn-in-source-health" element={renderProtectedElement(<BurnInSourceHealthPage />)} />
        <Route path="/burn-in-upload-comparison" element={renderProtectedElement(<BurnInUploadComparisonPage />)} />
        <Route path="/burn-in-units" element={renderProtectedElement(<BurnInUnitsPage />)} />
        <Route path="/burn-in-data-completeness" element={renderProtectedElement(<BurnInDataCompletenessPage />)} />
        <Route path="/burn-in-sequence-hotspots" element={renderProtectedElement(<BurnInSequenceHotspotsPage />)} />
        <Route path="/burn-in-sequence-bands" element={renderProtectedElement(<BurnInSequenceBandsPage />)} />
        <Route path="/burn-in-value-spread" element={renderProtectedElement(<BurnInValueSpreadPage />)} />
        <Route path="/burn-in-limit-compliance" element={renderProtectedElement(<BurnInLimitCompliancePage />)} />
        <Route path="/burn-in-near-limit-risk" element={renderProtectedElement(<BurnInNearLimitRiskPage />)} />
        <Route path="/burn-in-categorical-values" element={renderProtectedElement(<BurnInCategoricalValuesPage />)} />
        <Route 
          path="/pie-chart" 
          element={renderProtectedElement(<SerialPassFailChart />)} 
        />
        <Route 
          path="/file-table" 
          element={renderProtectedElement(<FileTable />)} 
        />
        <Route 
          path="/pareto-chart" 
          element={renderProtectedElement(<ParetoChart />)} 
        />
        <Route
          path="/normal-distribution"
          element={renderProtectedElement(<NormalDistributionChart />)}
        />
        <Route 
          path="/uut-status-summary" 
          element={renderProtectedElement(<UUTStatusSummaryChart />)} 
        />
        <Route 
          path="/control-chart" 
          element={renderProtectedElement(<ControlChart />)} 
        />

        {/* Catch-all route to redirect any unknown path to the dashboard or login */}
        <Route 
          path="*" 
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} 
        />
      </Routes>
    </Router>
  );
};

function AppActivityTracker({ isAuthenticated, onUnauthenticated }) {
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;

    const sendVisit = async () => {
      try {
        const result = await sendAuthPageVisit(
          `${location.pathname}${location.search}`,
          getClientMeta()
        );
        if (!cancelled && result.authenticated === false) {
          onUnauthenticated?.();
        }
      } catch (error) {
        console.warn('Page visit tracking failed:', error);
      }
    };

    sendVisit();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, location.pathname, location.search, onUnauthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const handlePageHide = () => {
      sendAuthDisconnect('pagehide', getClientMeta());
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isAuthenticated]);

  return null;
}

function AppDocumentTitle() {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const baseTitle = getDocumentTitleForPath(location.pathname);
    document.documentElement.dataset.smartiBaseTitle = baseTitle;
    document.title = baseTitle;
  }, [location.pathname]);

  return null;
}

export default App;
